import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/admin/admin-table.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function AdminAllies() {
  const navigate = useNavigate();

  const [allies, setAllies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("TODOS");
  const [pais, setPais] = useState("TODOS");

  useEffect(() => {
    loadAllies();
  }, []);

  const loadAllies = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/allies`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar la lista de aliados");
      }

      const data = responseText ? JSON.parse(responseText) : [];
      setAllies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar los aliados");
      setAllies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (ally) => {
    const confirmAction = window.confirm(
      `¿Seguro que quieres desactivar a ${ally.name} ${ally.lastName || ""}?`
    );

    if (!confirmAction) return;

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/allies/${ally.idUser}/deactivate`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const text = await response.text();

      if (!response.ok) {
        throw new Error(text || "Error al desactivar");
      }

      await loadAllies();
    } catch (err) {
      setError(err.message || "Ocurrió un error al desactivar el aliado");
    }
  };

  const handleReactivate = async (ally) => {
    const confirmAction = window.confirm(
      `¿Seguro que quieres reactivar a ${ally.name} ${ally.lastName || ""}?`
    );

    if (!confirmAction) return;

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/allies/${ally.idUser}/reactivate`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const text = await response.text();

      if (!response.ok) {
        throw new Error(text || "Error al reactivar");
      }

      await loadAllies();
    } catch (err) {
      setError(err.message || "Ocurrió un error al reactivar el aliado");
    }
  };

  const filteredAllies = useMemo(() => {
    return allies.filter((ally) => {
      const fullName = `${ally.name || ""} ${ally.lastName || ""}`.trim().toLowerCase();
      const email = (ally.email || "").toLowerCase();
      const dni = (ally.dni || "").toLowerCase();
      const phone = (ally.phone || "").toLowerCase();
      const country = (ally.country || "").toUpperCase();
      const status = (ally.status || "").toUpperCase();
      const searchValue = search.toLowerCase().trim();

      const matchesSearch =
        !searchValue ||
        fullName.includes(searchValue) ||
        email.includes(searchValue) ||
        dni.includes(searchValue) ||
        phone.includes(searchValue);

      const matchesEstado = estado === "TODOS" || status === estado;
      const matchesPais = pais === "TODOS" || country === pais;

      return matchesSearch && matchesEstado && matchesPais;
    });
  }, [allies, search, estado, pais]);

  return (
    <div className="admin-page">
      <div className="table-page-header">
        <div>
          <h2>Aliados</h2>
        </div>

        <button
          className="primary-btn"
          onClick={() => navigate("/admin/aliados/crear")}
        >
          Crear aliado
        </button>
      </div>

      <div className="table-toolbar">
        <input
          className="table-search"
          type="text"
          placeholder="Buscar por nombre, correo, cédula o teléfono"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="table-filters">
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="TODOS">Todos los estados</option>
            <option value="ACTIVE">Activos</option>
            <option value="INACTIVE">Inactivos</option>
          </select>

          <select value={pais} onChange={(e) => setPais(e.target.value)}>
            <option value="TODOS">Todos los países</option>
            <option value="CO">Colombia</option>
            <option value="EC">Ecuador</option>
          </select>

          <button type="button" className="secondary-btn" onClick={loadAllies}>
            Recargar
          </button>
        </div>
      </div>

      {error ? <p className="admin-form-message error">{error}</p> : null}

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre completo</th>
              <th>Correo</th>
              <th>Celular</th>
              <th>Cédula</th>
              <th>País</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="empty-row">
                  Cargando aliados...
                </td>
              </tr>
            ) : filteredAllies.length > 0 ? (
              filteredAllies.map((ally) => {
                const fullName = `${ally.name || ""} ${ally.lastName || ""}`.trim();
                const normalizedStatus = (ally.status || "").toUpperCase();
                const isInactive = normalizedStatus === "INACTIVE";

                return (
                  <tr key={ally.idUser}>
                    <td>{ally.idUser}</td>
                    <td>{fullName || "-"}</td>
                    <td>{ally.email || "-"}</td>
                    <td>{ally.phone || "-"}</td>
                    <td>{ally.dni || "-"}</td>
                    <td>{ally.country || "-"}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          normalizedStatus === "ACTIVE" ? "active" : "inactive"
                        }`}
                      >
                        {normalizedStatus || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => navigate(`/admin/aliados/${ally.idUser}`)}>
                          Ver
                        </button>

                        {isInactive ? (
                          <button onClick={() => handleReactivate(ally)}>
                            Reactivar
                          </button>
                        ) : (
                          <button onClick={() => handleDeactivate(ally)}>
                            Desactivar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="empty-row">
                  No se encontraron aliados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}